import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Mail, Phone, MessageCircle, CheckCircle2, XCircle, Pause, Loader2, Pencil } from "lucide-react";
import { useAuth, API } from "../lib/auth";

const PLAN_DEFAULT_CREDITS = { starter: 1, growth: 3, priority: 5, talent: 10 };

const STATUS_BADGE = {
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  suspended: "bg-slate-200 text-slate-700 border-slate-300",
  rejected: "bg-rose-100 text-rose-600 border-rose-200",
};

function whatsAppLink(phone) {
  if (!phone) return null;
  const cleaned = phone.replace(/\D/g, "");
  return `https://wa.me/${cleaned}`;
}

export default function AdminEmployerApprovals() {
  const { authHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // employer being approved
  const [form, setForm] = useState({ plan_slug: "growth", credits: 3, activation_date: "", admin_notes: "" });
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [editEmp, setEditEmp] = useState(null); // employer being renamed
  const [editForm, setEditForm] = useState({ company_name: "", contact_name: "", email: "", phone: "", website: "", sector: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/employers/pending`, { headers: authHeader, params: showAll ? { status: "all" } : {} });
      setItems(r.data.items || []);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showAll]);

  const openApprove = (emp) => {
    const slug = emp.requested_plan || "growth";
    setForm({
      plan_slug: slug,
      credits: PLAN_DEFAULT_CREDITS[slug] || 3,
      activation_date: new Date().toISOString().slice(0, 16),
      admin_notes: emp.admin_notes || "",
    });
    setSelected(emp);
  };

  const doApprove = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/employers/${selected.id}/approve`, {
        plan_slug: form.plan_slug,
        credits: parseInt(form.credits, 10) || PLAN_DEFAULT_CREDITS[form.plan_slug] || 1,
        activation_date: form.activation_date ? new Date(form.activation_date).toISOString() : null,
        admin_notes: form.admin_notes || null,
      }, { headers: authHeader });
      toast.success("Employer approved & plan activated");
      setSelected(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    finally { setBusy(false); }
  };

  const doReject = async (emp) => {
    const notes = window.prompt("Reason (optional):") || "";
    if (!window.confirm(`Reject ${emp.company_name}?`)) return;
    try {
      await axios.patch(`${API}/admin/employers/${emp.id}/reject`, { admin_notes: notes }, { headers: authHeader });
      toast.success("Rejected");
      load();
    } catch { toast.error("Reject failed"); }
  };

  const doSuspend = async (emp) => {
    if (!window.confirm(`Suspend ${emp.company_name}?`)) return;
    try {
      await axios.patch(`${API}/admin/employers/${emp.id}/suspend`, {}, { headers: authHeader });
      toast.success("Suspended");
      load();
    } catch { toast.error("Suspend failed"); }
  };

  const openEdit = (emp) => {
    setEditForm({
      company_name: emp.company_name || "",
      contact_name: emp.contact_name || emp.name || "",
      email: emp.email || "",
      phone: emp.phone || "",
      website: emp.website || "",
      sector: emp.sector || "",
    });
    setEditEmp(emp);
  };

  const doEdit = async () => {
    if (!editEmp) return;
    if (!editForm.company_name.trim()) { toast.error("Company name is required"); return; }
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/employers/${editEmp.id}`, editForm, { headers: authHeader });
      toast.success("Employer updated — changes propagated to jobs & applications");
      setEditEmp(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  const expiryPreview = useMemo(() => {
    if (!form.activation_date) return null;
    const d = new Date(form.activation_date);
    d.setDate(d.getDate() + 365);
    return d.toLocaleDateString();
  }, [form.activation_date]);

  return (
    <div className="space-y-6" data-testid="admin-employer-approvals">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold text-slate-900">Employer Approvals</h2>
          <p className="text-sm text-slate-500">Review new employer registrations, assign a plan, and activate the account.</p>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} data-testid="approvals-show-all" className="h-4 w-4 accent-[#1A8FE3]" />
          Show all employers (including active)
        </label>
      </div>

      {loading ? (
        <Card className="p-8 text-center text-slate-500">Loading…</Card>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center text-slate-500">No employers awaiting review.</Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((emp) => (
            <Card key={emp.id} className="p-5" data-testid={`emp-approval-${emp.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display font-semibold text-slate-900 truncate">{emp.company_name}</h3>
                    <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${STATUS_BADGE[emp.status] || STATUS_BADGE.pending}`}>{emp.status}</span>
                  </div>
                  <div className="text-sm text-slate-600 mt-0.5">{emp.contact_name || emp.name}</div>
                  {emp.sector && <div className="text-xs text-slate-500 mt-0.5">Sector: {emp.sector}</div>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {emp.email && <a href={`mailto:${emp.email}`} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Email"><Mail className="h-4 w-4" /></a>}
                  {emp.phone && <a href={`tel:${emp.phone}`} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Call"><Phone className="h-4 w-4" /></a>}
                  {emp.phone && <a href={whatsAppLink(emp.phone)} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-emerald-50 text-emerald-600" title="WhatsApp"><MessageCircle className="h-4 w-4" /></a>}
                </div>
              </div>

              <div className="mt-3 text-xs text-slate-500 space-y-0.5">
                {emp.email && <div><span className="text-slate-400">Email:</span> {emp.email}</div>}
                {emp.phone && <div><span className="text-slate-400">Phone:</span> {emp.phone}</div>}
                {emp.requested_plan && (
                  <div><span className="text-slate-400">Requested plan:</span> <span className="font-medium text-slate-700 capitalize">{emp.requested_plan_name || emp.requested_plan}</span> ({emp.requested_credits} credits)</div>
                )}
                <div><span className="text-slate-400">Registered:</span> {new Date(emp.created_at).toLocaleString()}</div>
              </div>

              {emp.recruitment_needs && (
                <div className="mt-3 rounded-lg bg-slate-50 border border-slate-100 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Recruitment needs</div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{emp.recruitment_needs}</p>
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                {emp.status !== "active" && (
                  <Button onClick={() => openApprove(emp)} data-testid={`btn-approve-${emp.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-8 px-4 text-xs">
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Approve & activate
                  </Button>
                )}
                {emp.status === "pending" && (
                  <Button onClick={() => doReject(emp)} data-testid={`btn-reject-${emp.id}`} variant="outline" className="rounded-full h-8 px-4 text-xs text-rose-600 border-rose-200 hover:bg-rose-50">
                    <XCircle className="h-3.5 w-3.5 mr-1" />Reject
                  </Button>
                )}
                {emp.status === "active" && (
                  <Button onClick={() => doSuspend(emp)} data-testid={`btn-suspend-${emp.id}`} variant="outline" className="rounded-full h-8 px-4 text-xs">
                    <Pause className="h-3.5 w-3.5 mr-1" />Suspend
                  </Button>
                )}
                <Button onClick={() => openEdit(emp)} data-testid={`btn-edit-${emp.id}`} variant="outline" className="rounded-full h-8 px-4 text-xs">
                  <Pencil className="h-3.5 w-3.5 mr-1" />Edit
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Approve dialog */}
      <Dialog open={selected !== null} onOpenChange={(v) => { if (!v) setSelected(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Approve — {selected?.company_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Plan</Label>
              <Select value={form.plan_slug} onValueChange={(v) => setForm({ ...form, plan_slug: v, credits: PLAN_DEFAULT_CREDITS[v] || 1 })}>
                <SelectTrigger data-testid="approve-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter Match (1 credit)</SelectItem>
                  <SelectItem value="growth">Growth Match (3 credits)</SelectItem>
                  <SelectItem value="priority">Priority Match (5 credits)</SelectItem>
                  <SelectItem value="talent">Talent Pipeline (10 credits)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Job credits</Label>
                <Input type="number" min={0} data-testid="approve-credits" value={form.credits} onChange={(e) => setForm({ ...form, credits: e.target.value })} />
              </div>
              <div>
                <Label>Activation date</Label>
                <Input type="datetime-local" data-testid="approve-activation" value={form.activation_date} onChange={(e) => setForm({ ...form, activation_date: e.target.value })} />
              </div>
            </div>
            {expiryPreview && (
              <p className="text-xs text-slate-500">Auto expiry: <span className="font-medium text-slate-700">{expiryPreview}</span> (12 months)</p>
            )}
            <div>
              <Label>Internal notes</Label>
              <Textarea rows={3} data-testid="approve-notes" value={form.admin_notes} onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} placeholder="Optional — visible to admins only." />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setSelected(null)}>Cancel</Button>
              <Button onClick={doApprove} disabled={busy} data-testid="approve-confirm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Approve & activate"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit employer dialog (rename + basic profile) */}
      <Dialog open={editEmp !== null} onOpenChange={(v) => { if (!v) setEditEmp(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit employer — {editEmp?.company_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Company name *</Label>
              <Input data-testid="edit-emp-name" value={editForm.company_name} onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })} />
              <p className="text-[11px] text-slate-500 mt-1">Renaming propagates to every job listing, application and search result.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Contact name</Label><Input value={editForm.contact_name} onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })} /></div>
              <div><Label>Sector</Label><Input value={editForm.sector} onChange={(e) => setEditForm({ ...editForm, sector: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
              <div><Label>Phone</Label><Input type="tel" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
            </div>
            <div>
              <Label>Website</Label>
              <Input value={editForm.website} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} placeholder="https://…" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditEmp(null)}>Cancel</Button>
              <Button onClick={doEdit} disabled={busy} data-testid="edit-emp-save" className="bg-[#1A8FE3] hover:bg-[#157AC0] text-white">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
